import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import lodash from "lodash";
import io from "socket.io-client";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import momentTimeZone from "moment-timezone";

import getEventDetails from "../../api/getEventDetail";
import getEventItemsListAPI from "../../api/getEventItemsListAPI";
import getBidStatusAPI from "../../api/getBidStatusAPI";
import getMyLotsBuyerAPI from "../../api/getMyLotsBuyerAPI";
import AuctionDetails from "../../packages/AuctionDetails";
import AuctionLotsContainer from "../../packages/AuctionLotsContainer";
import useStyles from "../components/Auction/auction.style";
import { getToken } from "../../common";
import config from "../../config/config";
import { motion } from "framer-motion";
import TermsAndCatalogDialog from "./TermsAndCatalogDialog";
import acceptEventTerms from "../../api/acceptTermsForEventAPI";
import saveEventCompany from "../../api/saveEventCompanyAPI";
import SidebarComponent from "./Sidebar";
import LoginDialog from "./LoginDialog";
import { getUserData } from "../../common";
import { toast } from "react-toastify";
import {
  applyServerTime,
  isBeforeNow,
  syncServerTime,
} from "../../common/dateTime";

// a participant who hasn't accepted the terms is asked to, unless the auction
// has ended (nothing left to bid on, and the catalogue is no longer served)
const needsTermsAcceptance = (eventDetails) =>
  eventDetails?.acceptedTerms === false && eventDetails?.status !== "ended";

// wait after a start countdown ends, so the server has passed that time too
const COUNTDOWN_RELOAD_DELAY_MS = 1500;
// a lot still "upcoming" after its start: ask again, a few times at most
const EARLY_RELOAD_DELAY_MS = 2000;
const MAX_EARLY_RELOADS = 3;

const AuctionDetailPage = () => {
  const location = useParams();
  const auctionId = location.auctionId || location.source;
  const classes = useStyles();
  const timeZone =
    localStorage.getItem("currentTimeZone") || momentTimeZone.tz.guess();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [socket, setSocket] = useState(null);
  // a ref, not state: callbacks saved in earlier renders (countdowns) must
  // see the current value, or a reload can be skipped for good
  const fetchingInProgress = useRef(false);
  const earlyReloads = useRef(0);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const SOCKET_URL = config.api.socket_url;

  const [details, updateDetails] = useState({});
  const [httpStatus, updateHttpStatus] = useState({
    auctionLots: false,
    auctionBuyerLots: false,
  });
  const [auctionAllLots, updateAuctionAllLots] = useState([]);
  const [auctionUserLots, updateAuctionUserLots] = useState([]);
  const [showTermsAndCatalog, setShowTermsAndCatalog] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  const userData = getUserData() ?? {};
  const isGuest = lodash.isEmpty(userData);
  // logged-out visitors are asked to sign in; they may close it and browse
  const [showLoginDialog, setShowLoginDialog] = useState(isGuest);

  useEffect(() => {
    loadInitialData();
  }, []);

  // one live connection per auction page, closed when the page is left (a
  // connection left open keeps receiving every bid update for nothing)
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      extraHeaders: { authorization: getToken() },
    });
    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
      // reloads still waiting on a timer are for this page only
      httpRequestRef.current = () => {};
    };
    // SOCKET_URL is fixed config
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket) return;

    if (userData) {
      socket.on(`${userData.userId}`, (props) => {
        socketListener(props);
      });
    } else if (details?.eventId) {
      socket.on(`${details?.eventId}`, (props) => {
        socketListener(props);
      });
    }

    // specific listener for lot updates (admin/seller changes)
    if (details?.eventId) {
      socket.emit("join-event", details.eventId); // Join the event room
      socket.on("LOT_UPDATED", (props) => {
        handleLotUpdate(props);
      });
    }

    return () => {
      if (userData) {
        socket.off(`${userData.userId}`);
      } else if (details?.eventId) {
        socket.off(`${details?.eventId}`);
      }
      if (details?.eventId) {
        socket.off("LOT_UPDATED");
      }
    };
  }, [userData, socket, details?.eventId]);

  // Internet back after a drop: the live connection reconnects by itself, but
  // updates sent meanwhile were missed and the clock may be off. Re-check the
  // server time, re-join the auction room and reload the lots, once.
  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => {
      syncServerTime();
      if (details?.eventId) socket.emit("join-event", details.eventId);
      httpRequest();
    };
    socket.io.on("reconnect", onReconnect);
    return () => {
      socket.io.off("reconnect", onReconnect);
    };
    // httpRequest is recreated every render; re-subscribing on each one isn't needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, details?.eventId]);

  const handleLotUpdate = (props) => {
    const updateLogic = (prevLots) => {
      return lodash.map(lodash.cloneDeep(prevLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }
        // Merge updated fields
        return { ...details, ...props };
      });
    };

    updateAuctionAllLots((prevLots) => updateLogic(prevLots));
    updateAuctionUserLots((prevUserLots) => updateLogic(prevUserLots));
  };

  const loadInitialData = async () => {
    setLoadingInitialData(true);
    await httpRequest();
    setLoadingInitialData(false);
  };

  const updateListWithBidStatusAllotedLots = (auctionAllotedLots, listData) => {
    return lodash.map(listData, (details) => {
      const searchAuctionLot = lodash.find(
        auctionAllotedLots,
        ({ eventItemId }) => eventItemId === details.eventItemId
      );
      if (lodash.isUndefined(searchAuctionLot)) {
        details.myBidStatus = {
          myLastBid: 0,
          myBidRank: "0",
          status: "error",
          authorized: false,
        };
      } else {
        details.myBidStatus = searchAuctionLot.myBidStatus;
      }

      return details;
    });
  };

  const updateListWithDefaultBidStatus = (listData) =>
    listData.map((details) => ({
      ...details,
      myBidStatus: {
        myLastBid: 0,
        myBidRank: "0",
        status: "error",
        authorized: false,
      },
    }));

  const updateListWithBidStatus = async (listData) =>
    Promise.all(
      listData.map(async (object) => {
        try {
          const { data } = await getBidStatusAPI(
            object.eventId,
            object.eventItemId,
            { timeZone }
          );
          return { ...object, myBidStatus: data };
        } catch (error) {
          return {
            ...object,
            myBidStatus: {
              myLastBid: 0,
              myBidRank: "0",
              status: "error",
              authorized: true,
            },
          };
        }
      })
    );

  const httpRequest = async () => {
    try {
      if (fetchingInProgress.current) return;
      fetchingInProgress.current = true;
      const data = { auctionLots: [], auctionBuyerLots: [] };
      const detailsResponse = await getEventDetails(
        { auctionId: auctionId },
        { timeZone }
      );

      if (detailsResponse.status === 200) {
        updateDetails({ ...detailsResponse.data });
        // check if buyer have accepted the terms and conditions
        // (not asked once the auction has ended)
        if (needsTermsAcceptance(detailsResponse.data)) {
          // terms and conditions not accepted
          setShowTermsAndCatalog(true);
          return;
        }
      }
      const listResponse = await getEventItemsListAPI(
        { itemId: auctionId },
        { timeZone }
      );

      if (listResponse.status === 200) {
        if (lodash.isEmpty(userData)) {
          data.auctionLots = updateListWithDefaultBidStatus(
            listResponse.data.data
          );
        } else {
          const { status, data: response } = await getMyLotsBuyerAPI(
            {
              eventId: auctionId,
            },
            { timeZone }
          );

          if (status !== 200) {
            data.auctionLots = updateListWithDefaultBidStatus(
              listResponse.data.data
            );
          } else {
            data.auctionBuyerLots = await updateListWithBidStatus(
              response.data ?? []
            );
            data.auctionLots = updateListWithBidStatusAllotedLots(
              data.auctionBuyerLots,
              listResponse.data.data
            );
          }
        }
        updateAuctionAllLots([...data.auctionLots]);
        updateAuctionUserLots([...data.auctionBuyerLots]);
        updateHttpStatus({ auctionLots: true, auctionBuyerLots: true });

        // a lot past its start time that the server still calls upcoming:
        // this reload reached the server a moment early, so ask again
        const startedEarly = [
          ...data.auctionLots,
          ...data.auctionBuyerLots,
        ].some(
          (lot) =>
            lot.status === "upcoming" && isBeforeNow(lot.startDateAndTime)
        );
        if (!startedEarly) {
          earlyReloads.current = 0;
        } else if (earlyReloads.current < MAX_EARLY_RELOADS) {
          earlyReloads.current += 1;
          setTimeout(() => httpRequestRef.current(), EARLY_RELOAD_DELAY_MS);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      fetchingInProgress.current = false;
    }

    return true;
  };
  // timers call the latest httpRequest, not the one from when they were set
  const httpRequestRef = useRef(httpRequest);
  httpRequestRef.current = httpRequest;

  // a countdown reached a lot's start (or the end of prebid). It rounds to
  // the nearest second, so it can finish ~0.5 s before the server's clock:
  // reloading at once would still get the lot as upcoming.
  const reloadAfterCountdown = () => {
    setTimeout(() => httpRequestRef.current(), COUNTDOWN_RELOAD_DELAY_MS);
  };

  const handleEnglishAuctionSocket = (props) => {
    updateAuctionAllLots((prevLots) => {
      return lodash.map(lodash.cloneDeep(prevLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }

        details.myBidStatus = props;
        details.highestBid = props.highestBid;
        details.nextBidAmount = props.nextBidAmount;
        details.endDateAndTime = props.updatedEndTime;

        return { ...details };
      });
    });

    updateAuctionUserLots((prevUserLots) => {
      return lodash.map(lodash.cloneDeep(prevUserLots), (details) => {
        if (details.eventItemId !== props.eventItemId) {
          return details;
        }

        details.myBidStatus = props;
        details.highestBid = props.highestBid;
        details.nextBidAmount = props.nextBidAmount;
        details.endDateAndTime = props.updatedEndTime;

        return { ...details };
      });
    });
  };

  const handleDutchAuctionSocket = (props) => {
    const receivedLots = props?.data || [];
    const structuredReceivedLots = {};

    receivedLots.forEach((lot) => {
      structuredReceivedLots[lot.eventItemId] = {
        ...lot,
      };
    });

    updateAuctionAllLots((prevLots) => {
      return prevLots
        .map((details) => {
          return {
            ...details,
            ...(structuredReceivedLots[details.eventItemId] || {}),
          };
        })
        .filter((lot) => lot.concluded !== "YES");
    });

    updateAuctionUserLots((prevUserLots) => {
      return prevUserLots
        .map((details) => {
          return {
            ...details,
            ...(structuredReceivedLots[details.eventItemId] || {}),
          };
        })
        .filter((lot) => lot.concluded !== "YES");
    });
  };

  const handleJapaneseAuctionSocket = (props) => {
    const receivedLots = props?.data || [];
    const structuredReceivedLots = {};

    receivedLots.forEach((lot) => {
      structuredReceivedLots[lot.eventItemId] = {
        ...lot,
      };
    });

    updateAuctionAllLots((prevLots) => {
      return prevLots.map((details) => {
        return {
          ...details,
          ...(structuredReceivedLots[details.eventItemId] || {}),
        };
      });
    });

    updateAuctionUserLots((prevUserLots) => {
      return prevUserLots.map((details) => {
        return {
          ...details,
          ...(structuredReceivedLots[details.eventItemId] || {}),
        };
      });
    });
  };

  const socketListener = (props) => {
    try {
      if (!props) return;
      // every live update carries the server's time: stay matched to it
      applyServerTime(props.serverTime);

      switch (details?.eventFormat) {
        case "ENGLISH_AUCTION":
          handleEnglishAuctionSocket(props);
          break;
        case "DUTCH_AUCTION":
          handleDutchAuctionSocket(props);
          break;
        case "JAPANESE_AUCTION":
          handleJapaneseAuctionSocket(props);
          break;
        default:
          break;
      }
    } catch (error) {
      console.log(error);
    }
  };

  // filter lot list and remove the lot which has been ended
  const removeEndedLot = (id) => {
    const updatedAllLots = auctionAllLots?.filter(
      (lot) => lot.eventItemId != id
    );

    const updatedUserLots = auctionUserLots?.filter(
      (lot) => lot.eventItemId != id
    );

    updateAuctionAllLots(updatedAllLots);
    updateAuctionUserLots(updatedUserLots);
  };

  // update lot list & add finalize flag
  const finalizeLot = (id) => {
    const updatedAllLots = auctionAllLots?.map((lot) => {
      if (lot.eventItemId != id) return lot;
      else {
        return {
          ...lot,
          finalizing: true,
        };
      }
    });

    const updatedUserLots = auctionUserLots?.map((lot) => {
      if (lot.eventItemId != id) return lot;
      else {
        return {
          ...lot,
          finalizing: true,
        };
      }
    });

    updateAuctionAllLots(updatedAllLots);
    updateAuctionUserLots(updatedUserLots);

    setTimeout(() => {
      // remove lot
      removeEndedLot(id);
    }, 5000);
  };

  const fetchSingleLotBidStatus = async (eventId, eventItemId) => {
    // MANDATORY preview period: the first bid unlocks the hidden lot details
    // (start price, lead bid), so reload the lots instead of only the status
    const placedFirstBid = [...auctionAllLots, ...auctionUserLots].some(
      (lot) => lot.eventItemId === eventItemId && lot.firstBidRequired
    );
    if (placedFirstBid) {
      return httpRequest();
    }

    try {
      const { data } = await getBidStatusAPI(eventId, eventItemId, {
        timeZone,
      });

      const updateLogic = (prevLots) => {
        return prevLots.map((lot) => {
          if (lot.eventItemId === eventItemId) {
            return {
              ...lot,
              myBidStatus: data,
            };
          }
          return lot;
        });
      };

      updateAuctionAllLots((prev) => updateLogic(prev));
      updateAuctionUserLots((prev) => updateLogic(prev));
    } catch (error) {
      console.error("Error fetching single lot bid status:", error);
    }
  };

  // the company form's Next: true when saved, so the dialog moves on
  const handleSaveCompany = async (company, ipAddress) => {
    try {
      await saveEventCompany({ auctionId, company, ipAddress });
      return true;
    } catch (error) {
      console.log(error);
      const reply = error.response?.data;
      toast(
        reply?.error ||
          (typeof reply === "string" && reply) ||
          "Couldn't save your details. Try again."
      );
      return false;
    }
  };

  const handleTermsAndCatalogDialogClose = async (
    accepted,
    ipAddress,
    company
  ) => {
    if (!accepted) {
      return;
    }

    // if accepted, update the details and set showTermsAndCatalog to false
    try {
      setAcceptingTerms(true);
      // accept terms and conditions
      // 1. make API call to accept terms and conditions (with the company
      //    details the bidder participates with)
      const _acceptTermsResponse = await acceptEventTerms({
        auctionId,
        ipAddress,
        company,
      });

      // 2. update the details state with the new data
      // 3. set showTermsAndCatalog to false
      setShowTermsAndCatalog(false);
      // 4. load only lots data
      loadInitialData();
    } catch (error) {
      console.log(error);
      const reply = error.response?.data;
      toast(
        reply?.error ||
          (typeof reply === "string" && reply) ||
          "Couldn't save your acceptance. Try again."
      );
    } finally {
      setAcceptingTerms(false);
    }
  };

  if (loadingInitialData) {
    return (
      <Box className={classes.auction}>
        <Box className={classes.auctionDetail}>
          <Box className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "30vh",
                width: "100%",
                textAlign: "center",
              }}
            >
              <CircularProgress
                sx={{
                  color: "#2D55EB",
                  width: "5rem",
                  height: "5rem",
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "#444",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Just a moment... fetching auction details
              </Typography>
              <Typography variant="body2" sx={{ color: "#888", mt: 1 }}>
                We’re gathering the latest data for you.
              </Typography>
            </motion.div>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <div className={classes.auction}>
      <SidebarComponent mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Box className={classes.auctionDetail}>
        <Box className="container">
          {isGuest && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
              <Button
                variant="contained"
                onClick={() => setShowLoginDialog(true)}
                sx={{ backgroundColor: "#2D55EB", borderRadius: 0 }}
              >
                Sign in to bid
              </Button>
            </Box>
          )}
          <LoginDialog
            open={isGuest && showLoginDialog}
            onClose={() => setShowLoginDialog(false)}
            onLogin={() => window.location.reload()}
            redirectPath={`/auctions/${auctionId}`}
          />
          <AuctionDetails
            eventsDetails={details}
            onPrebidEnd={reloadAfterCountdown}
          ></AuctionDetails>
          <TermsAndCatalogDialog
            show={needsTermsAcceptance(details)}
            handleClose={handleTermsAndCatalogDialogClose}
            saveCompany={handleSaveCompany}
            catalog={details?.auctionCatalogueUrl}
            prefillCompany={details?.prefillCompany}
            auctioneerName={details?.auctioneerName}
            submitting={acceptingTerms}
          />
          <AuctionLotsContainer
            allLots={auctionAllLots}
            userLots={auctionUserLots}
            eventsDetails={details}
            refetchLotDetails={httpRequest}
            reloadAfterCountdown={reloadAfterCountdown}
            finalizeLot={finalizeLot}
            auctionId={auctionId}
            fetchSingleLotBidStatus={fetchSingleLotBidStatus}
          />
        </Box>
      </Box>
    </div>
  );
};

// Another auction's id in the address (only the part after # changes, so the
// page isn't reloaded) opens the page fresh: no lots, details or live
// connection of the previous auction carry over.
const AuctionDetailRoute = () => {
  const { auctionId, source } = useParams();
  return <AuctionDetailPage key={auctionId || source} />;
};

export default AuctionDetailRoute;
